<template>
  <el-dialog
    id="create-modal"
    class="form-modal"
    :visible.sync="createVisible"
    v-loading="createProcessing"
    :fullscreen="true"
    :show-close="false"
    :modal="false"
  >
    <div id="create-modal-header" class="modal-header">
      <div class="modal-header-layout flex grid-6 align-center">
        <h1 class="modal-title col-2">Create Time of Day</h1>
        <div class="modal-actions col-2">
          <el-button type="primary" @click="createOperation">Create</el-button>
          <el-button type="default" @click="cancelOperation">Cancel</el-button>
        </div>
      </div>
    </div>

    <el-form>
      <div class="inline-form-group">
        <el-col :span="11">
          <el-input v-model="createForm.name" placeholder="Name">
            <template slot="prefix">Name</template>
          </el-input>
        </el-col>
        <el-col :span="1" />
        <el-col :span="12">
          <dads-select
            v-model="createForm.timezone"
            placeholder="Select a time zone"
            allow-create
            filterable
          >
            <template slot="prefix">Time zone</template>
            <el-option label="Use tenant default" value="" />
            <el-option
              v-for="(timezone, index) in timezones"
              :key="index"
              :label="timezone.name"
              :value="timezone.name"
            />
          </dads-select>
        </el-col>
      </div>
      <el-select v-model="createForm.type" placeholder="Select type">
        <el-option
          v-for="(item, i) in customTypeOptions"
          :key="i"
          :label="item.label"
          :value="item.value"
        />
        <template slot="prefix">Type</template>
      </el-select>

      <!-- ⭐ WEEKTIME ⭐ -->
      <div v-if="customType === 'WEEKTIME'">
        <div class="inline-form-group">
          <el-radio-group
            v-model="weekTimeType"
            @change="toggleWeekTimeFields"
            class="mv-10 black-text"
          >
            <el-radio label="preset"
              ><span class="black-text">Mon - Fri preset</span></el-radio
            >
            <el-radio label="presetWeek"
            ><span class="black-text">Sun - Sat preset</span></el-radio
            >
            <el-radio label="custom"
              ><span class="black-text">Custom</span></el-radio
            >
          </el-radio-group>
        </div>

        <div v-if="createForm.preset.checked">
          <h3 class="form-section-header">Select time range</h3>
          <div class="inline-form-group">
            <el-col :span="12">
              <omnia-time-select
                @onTimeSelected="createForm.preset.startTime = $event"
                start="00:00"
                end="23:59"
                step="00:05"
                title="Start time"
              ></omnia-time-select>
            </el-col>
            <el-col :span="1" />
            <el-col :span="11">
              <omnia-time-select
                @onTimeSelected="createForm.preset.endTime = $event"
                start="00:00"
                end="23:59"
                step="00:05"
                title="End time"
                :minTime="createForm.preset.startTime"
              />
            </el-col>
          </div>
        </div>

        <div v-if="createForm.presetWeek.checked">
          <h3 class="form-section-header">Select time range</h3>
          <div class="inline-form-group">
            <el-col :span="12">
              <omnia-time-select
                @onTimeSelected="createForm.presetWeek.startTime = $event"
                start="00:00"
                end="23:59"
                step="00:05"
                title="Start time"
              ></omnia-time-select>
            </el-col>
            <el-col :span="1" />
            <el-col :span="11">
              <omnia-time-select
                @onTimeSelected="createForm.presetWeek.endTime = $event"
                start="00:00"
                end="23:59"
                step="00:05"
                title="End time"
                :minTime="createForm.presetWeek.startTime"
              />
            </el-col>
          </div>
        </div>

        <div class="week-time" v-if="createForm.customWeekTime.checked">
          <h2 class="black-text form-section-header">
            Use the checkbox to enable a day
          </h2>
          <div
            class="inline-form-group align-center"
            v-for="(day, index) in days"
            :key="index"
          >
            <el-col :span="1" style="align-self: start; margin-top: 20px;">
              <el-checkbox
                @change="
                  createForm.customWeekTime[`day${index}`].enabled = $event
                "
                :value="createForm.customWeekTime[`day${index}`].enabled"
              />
            </el-col>
            <el-col :span="1" style="align-self: start; margin-top: 26px">
              <h4 class="black-text mt-0">{{ day }}</h4>
            </el-col>
            <el-col :span="1"></el-col>
            <el-col :span="21">
              <multi-time-range-selector
                :values="createForm.customWeekTime[`day${index}`].values"
                :disabled="!createForm.customWeekTime[`day${index}`].enabled"
                @timeRangeSelected="onTimeRangeSelection(index, $event)"
              />
            </el-col>
          </div>
        </div>
      </div>

      <!-- ⭐ CALENDAR ⭐ -->
      <div v-if="customType === 'CALENDAR'">
        <h2 class="form-section-header">Select date ranges</h2>
        <div class="mt-10">
          <div
            class="date-picker inline-form-group"
            v-for="(item, i) in dateRanges"
            :key="i"
          >
            <el-col :span="21">
              <el-date-picker
                :clearable="true"
                v-model="dateRanges[i]"
                type="daterange"
                format="yyyy-MM-dd"
                align="right"
                unlink-panels
                range-separator="To"
                start-placeholder="Start date"
                end-placeholder="End date"
                @input="onDateRangeSelection($event, i)"
              >
              </el-date-picker
            ></el-col>
            <el-col :span="1"></el-col>
            <el-col :span="2">
              <el-tooltip
                class="item"
                effect="dark"
                :content="`Remove date range ${i + 1}`"
                placement="top"
              >
                <el-button
                  @click="removeDateRange(i)"
                  class="table-action-button icon-red"
                  icon="el-icon-delete"
                ></el-button>
              </el-tooltip>
            </el-col>
          </div>
        </div>
        <div class="inline-form-group">
          <el-button style="float: right;" @click="addDateRange"
            >Add date range</el-button
          >
        </div>
      </div>

      <!-- ⭐ ALL or ANY ⭐ -->
      <div v-if="['ALL', 'ANY'].includes(customType)">
        <h2 class="form-section-header">Select conditions</h2>
        <dads-select
          v-model="createForm.conditionParams"
          placeholder="Select global conditions"
          multiple
          filterable
        >
          <template slot="prefix"
            ><span style="opacity: 0">Conditions</span></template
          >
          <el-option
            v-for="(item, i) in conditionOptions"
            :key="i"
            :label="item.name"
            :value="item.id"
          />
        </dads-select>
      </div>
    </el-form>
  </el-dialog>
</template>

<script>
import OmniaTimeSelect from "@/components/OmniaTimeSelect.vue";
import DadsSelect from "@/components/select";
import moment from "moment";
import MultiTimeRangeSelector from "../../time-of-days/components/multiTimeRangeSelector.vue";

export default {
  name: "GlobalTimeOfDaysCreateForm",

  props: {
    createVisible: { required: true },
    clearFormFields: { required: true },
    createProcessing: { required: true },
    timezones: { required: true },
    customTypeOptions: { required: true },
    globalConditionOptions: { required: true },
    conditionOptions: { required: true }
  },

  components: {
    OmniaTimeSelect,
    DadsSelect,
    MultiTimeRangeSelector
  },

  data() {
    return {
      activeTabName: "preset",
      weekTimeType: null,
      days: ["Sun", "Mon", "Tues", "Wed", "Thurs", "Fri", "Sat"],

      createForm: {
        tenantId: 0,
        name: "",
        timezone: "",
        type: "",
        param1: "",
        conditionParams: [],
        preset: {
          checked: false,
          startTime: "",
          endTime: ""
        },
        presetWeek: {
          checked: false,
          startTime: "",
          endTime: ""
        },
        customWeekTime: {
          checked: false,
          day0: {
            enabled: false,
            values: []
          },
          day1: {
            enabled: false,
            values: []
          },
          day2: {
            enabled: false,
            values: []
          },
          day3: {
            enabled: false,
            values: []
          },
          day4: {
            enabled: false,
            values: []
          },
          day5: {
            enabled: false,
            values: []
          },
          day6: {
            enabled: false,
            values: []
          }
        },
        matchDestinations: [],
        notMatchDestinations: [],
        dids: [],
        dateRanges: []
      },

      dateRanges: [[moment(), moment()]]
    };
  },

  computed: {
    customType() {
      return this.createForm.type;
    }
  },

  watch: {
    activeTabName(val) {
      if (val) {
        if (val === "preset") {
          this.initCustomWeekTimeData();
        } else if (val === "customWeekTime") {
          this.createForm.preset = {
            checked: false,
            startTime: "",
            endTime: ""
          };
        }
      }
    },

    customType(val) {
      if (val !== "USEGLOBAL") {
        this.createForm.param1 = "";
      }

      if (val !== "CALENDAR") {
        this.dateRanges = [[moment(), moment()]];
        this.createForm.dateRanges = [];
      }

      if (val !== "ALL" || val !== "ANY") {
        this.createForm.conditionParams = [];
      }

      if (val !== "WEEKTIME") {
        this.initCustomWeekTimeData();
      }
    },

    clearFormFields(value) {
      if (value) {
        this.clearFields();
      }
    }
  },

  methods: {
    onTimeRangeSelection(index, ranges) {
      this.createForm.customWeekTime[`day${index}`].values = [...ranges];
    },

    initCustomWeekTimeData() {
      this.createForm.customWeekTime = {
        checked: false,
        day0: {
          enabled: false,
          values: []
        },
        day1: {
          enabled: false,
          values: []
        },
        day2: {
          enabled: false,
          values: []
        },
        day3: {
          enabled: false,
          values: []
        },
        day4: {
          enabled: false,
          values: []
        },
        day5: {
          enabled: false,
          values: []
        },
        day6: {
          enabled: false,
          values: []
        }
      };
    },

    toggleWeekTimeFields(val) {

      if (val === "preset") {
        this.createForm.preset.checked = true;
        this.createForm.customWeekTime.checked = false;
        this.createForm.presetWeek = {
          checked: false,
          startTime: "",
          endTime: ""
        }
        return;
      }

      if(val === 'presetWeek'){
        this.createForm.presetWeek.checked = true;
        this.createForm.customWeekTime.checked = false;
        this.createForm.preset = {
          checked: false,
          startTime: "",
          endTime: ""
        };
        return;
      }

      if (val === "custom") {
        this.createForm.customWeekTime.checked = true;
        this.createForm.preset = {
          checked: false,
          startTime: "",
          endTime: ""
        };
        this.createForm.presetWeek = {
          checked: false,
          startTime: "",
          endTime: ""
        }
      }
    },

    onDateRangeSelection($event, idx) {
      const startDate = moment($event[0]).format("YYYY-MM-DD HH:mm:ss");
      const endDate = moment($event[1]).format("YYYY-MM-DD HH:mm:ss");
      this.createForm.dateRanges[idx] = {
        startDate,
        endDate
      };
    },

    addDateRange() {
      this.dateRanges.push([moment(), moment()]);
    },

    removeDateRange(idx) {
      this.dateRanges.splice(idx, 1);
      this.createForm.dateRanges.splice(idx, 1);
      if (this.dateRanges.length === 0) {
        this.dateRanges.push([moment(), moment()]);
      }
    },

    createOperation() {
      const createBody = { ...this.createForm };

      this.$emit("createOperation", createBody);
    },

    cancelOperation() {
      this.$emit("cancelOperation", "create");
    },

    clearFields() {
      this.activeTabName = "preset";
      this.weekTimeType = null;

      this.createForm = {
        tenantId: 0,
        name: "",
        timezone: "",
        type: "",
        param1: "",
        conditionParams: [],
        preset: {
          checked: false,
          startTime: "",
          endTime: ""
        },
        presetWeek: {
          checked: false,
          startTime: "",
          endTime: ""
        },
        customWeekTime: {
          checked: false,
          day0: {
            enabled: false,
            values: []
          },
          day1: {
            enabled: false,
            values: []
          },
          day2: {
            enabled: false,
            values: []
          },
          day3: {
            enabled: false,
            values: []
          },
          day4: {
            enabled: false,
            values: []
          },
          day5: {
            enabled: false,
            values: []
          },
          day6: {
            enabled: false,
            values: []
          }
        },
        matchDestinations: [],
        notMatchDestinations: [],
        dids: [],
        dateRanges: []
      };

      this.dateRanges = [[moment(), moment()]];
    }
  },

  filters: {
    formatNumber(value) {
      return `(${value.substr(0, 3)}) ${value.substr(3, 3)}-${value.substr(
        6,
        4
      )}`;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../../../assets/scss/variables";

.el-date-editor {
  width: 100%;
}

.date-picker {
  margin-bottom: 1.5rem;
}

.inline-form-group .el-checkbox {
  margin-left: 0;
}
</style>
